//@flow
import {Typography, makeStyles} from '@material-ui/core';
import React, {useEffect, useState} from 'react';
import {colors} from '../../../../../assets/styles/colors';
import XmarkIcon from '../../../../../elements/icons/XmarkIcon';
import ArrowLeftIcon from '../../../../../elements/icons/ArrowLeftIcon';
import ArrowRightIcon from '../../../../../elements/icons/ArrowRightIcon';
import RefreshIcon from '../../../../../elements/icons/RefreshIcon';
import LockIcon from '../../../../../elements/icons/LockIcon';
import {Add} from '@material-ui/icons';
import {useStoreDomain, useStoreInfo} from '../../../../store/utils';
import {useStoreUiInformation} from '../../../../ui/utils';
import {useIsFeatureInTier} from '../../../../../utils/component';
import {useTierRestrictionActions} from '../../../../TierRestriction/utils';
import {compressImageAdvanced} from '../../../../../utils/image';

const useStyles = makeStyles(theme => ({
  faviconSection: {
    display: 'flex',
    flexDirection: 'column-reverse',
    background: '#f8f9fb',
    borderRadius: '.75rem',
    padding: '1rem',
    gap: '1rem',

    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
    },
  },
  previewSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
    overflow: 'auto',

    [theme.breakpoints.up('md')]: {
      overflow: 'hidden',
      flexGrow: 1,
      width: '70%',
    },
  },
  searchResultContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    padding: '.75rem',
    background: 'white',
    borderRadius: '.75rem',
    minWidth: 'min-content',
    width: '100%',
  },
  searchResultHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',

    '& img': {
      width: '2.5rem',
      height: '2.5rem',
      borderRadius: '50%',
      objectFit: 'cover',
    },
  },
  searchResultTitle: {
    fontSize: '.825rem',
    fontWeight: 500,
    color: colors.grey900,
  },
  searchResultUrl: {
    fontSize: '.75rem',
    color: colors.grey700,
  },
  searchResultDescriptionTitle: {
    fontSize: '1.25rem',
    fontWeight: 500,
    color: '#2E24A3',
  },
  searchResultDescriptionText: {
    fontSize: '.875rem',
    color: colors.grey700,
  },

  browserViewContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
  },
  browserViewHeader: {
    display: 'flex',
    padding: '.5rem',
    borderRadius: '.75rem',
    background: 'white',
    alignItems: 'center',
    gap: '.5rem',
    width: 'auto',
    alignSelf: 'flex-start',

    '& img': {
      width: '1.75rem',
      height: '1.75rem',
      objectFit: 'cover',
    },
  },
  browserViewTitle: {
    fontSize: '.875rem',
    color: colors.grey900,
    paddingRight: '1rem',
  },
  browserViewCloseIcon: {
    fontSize: '1.25rem',
    color: colors.grey900,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  browserViewContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '.75rem',
    padding: '.5rem .75rem',
    borderRadius: '1rem',
    background: 'white',
    minWidth: 'min-content',
    width: '100%',
  },
  browserViewButtonIcon: {
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  browserViewUrlBar: {
    borderRadius: '2rem',
    padding: '.5rem 1rem .5rem .5rem',
    display: 'flex',
    alignItems: 'center',
    gap: '.25rem',
    background: '#F0F0F0',
    width: '100%',
    fontSize: '.875rem',
    color: colors.grey900,
  },
  browserViewUrlIcon: {
    fontSize: '1rem',
    display: 'flex',
    color: colors.success,
    alignItems: 'center',
    justifyContent: 'center',
  },
  uploadSection: {
    width: '30%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    margin: '0 auto',
    gap: '.5rem',
  },

  uploadSectionLabel: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    aspectRatio: '1/1',
    height: '100%',
    maxHeight: '250px',
    cursor: 'pointer',
    position: 'relative',
    background: 'white',
    borderRadius: '.75rem',

    [theme.breakpoints.up('md')]: {
      maxHeight: '180px',
    },
    [theme.breakpoints.up('lg')]: {
      maxHeight: '200px',
    },
  },
  uploadSectionLabelImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: '.75rem',
  },
  uploadSectionLabelImageSize: {
    fontSize: '.875rem',
    color: colors.grey700,
    fontWeight: 500,
    whiteSpace: 'nowrap',
    background: 'white',
    padding: '.25rem .5rem',
    borderRadius: '.5rem',
  },
}));

export type FaviconSectionProps = {
  onChangeFavicon: (file: File) => void,
  defaultFaviconUrl?: string,
};

export default function FaviconSection(props: FaviconSectionProps) {
  const {onChangeFavicon, defaultFaviconUrl} = props;

  const hasStoreFavicon = useIsFeatureInTier('STORE_FAVICON');
  const {showTierRestrictionModal} = useTierRestrictionActions();

  const classes = useStyles();

  const [faviconUrl, setFaviconUrl] = useState<string | null>(null);

  useEffect(() => {
    setFaviconUrl(defaultFaviconUrl || null);
  }, [defaultFaviconUrl]);

  const {domain} = useStoreDomain();
  const store = useStoreInfo();
  const storeUiInformation = useStoreUiInformation();

  const handleFaviconFileChange = async (e: any) => {
    const file = e.target.files[0];

    if (!file) return;
    if (!hasStoreFavicon) {
      showTierRestrictionModal();
      return;
    }
    const [, compressedFile] = await compressImageAdvanced(file, {
      quality: 1,
      maxHeight: 512,
      maxWidth: 512,
    });
    onChangeFavicon(compressedFile);

    const reader = new FileReader();
    reader.onload = (e: any) => {
      setFaviconUrl(e.target?.result);
    };

    reader.readAsDataURL(compressedFile);
  };

  if (!domain || !store) return null;

  return (
    <aside className={classes.faviconSection}>
      <div className={classes.previewSection}>
        <div className={classes.browserViewContainer}>
          <header className={classes.browserViewHeader}>
            <img src={faviconUrl || '/favicon.png'} alt="" />

            <Typography className={classes.browserViewTitle}>
              {store.name}
            </Typography>

            <span className={classes.browserViewCloseIcon}>
              <XmarkIcon />
            </span>
          </header>

          <div className={classes.browserViewContent}>
            <span className={classes.browserViewButtonIcon}>
              <ArrowLeftIcon />
            </span>
            <span className={classes.browserViewButtonIcon}>
              <ArrowRightIcon />
            </span>
            <span className={classes.browserViewButtonIcon}>
              <RefreshIcon />
            </span>

            <div className={classes.browserViewUrlBar}>
              <span className={classes.browserViewUrlIcon}>
                <LockIcon />
              </span>
              <span>https://{domain.domainName}</span>
            </div>
          </div>
        </div>
        <div className={classes.searchResultContainer}>
          <header className={classes.searchResultHeader}>
            <img src={faviconUrl || '/favicon.png'} alt="" />
            <div>
              <Typography className={classes.searchResultTitle}>
                {store.name}
              </Typography>
              <Typography className={classes.searchResultUrl}>
                https://{domain.domainName}
              </Typography>
            </div>
          </header>

          <div className={classes.searchResultDescription}>
            <Typography className={classes.searchResultDescriptionTitle}>
              {store.name}
            </Typography>
            <Typography className={classes.searchResultDescriptionText}>
              {storeUiInformation?.description ||
                'No hay descripción de la tienda'}
            </Typography>
          </div>
        </div>
      </div>
      <div className={classes.uploadSection}>
        <label
          className={classes.uploadSectionLabel}
          aria-label="Subir imagen del favicon (formato de imagen)"
        >
          <input
            type="file"
            accept="image/*"
            hidden
            id="favicon-input-file"
            onChange={handleFaviconFileChange}
          />
          {faviconUrl ? (
            <img
              className={classes.uploadSectionLabelImage}
              src={faviconUrl}
              alt="Favicon"
            />
          ) : (
            <>
              <Add />
              <Typography
                variant="body2"
                className={classes.uploadSectionLabelText}
              >
                Subir imagen
              </Typography>
            </>
          )}
        </label>
        <span className={classes.uploadSectionLabelImageSize}>256 x 256</span>
      </div>
    </aside>
  );
}
